/* STATUS / AGENT NOTES (2026-09-11)
 * Agent START: Clean collection addresses with server-filtered results.
 * Catalog membership and publication remain unchanged. Native section HTML owns results.
 * Agent 2026-09-11: Compact-filter failure recovery remains bounded after clean-route HTML replacement.
 * Agent 2026-09-11: Reuse the parser-started section request without duplicate downloads.
 */
(() => {
  if (window.ScoochCollectionRoutes) return;
  class CollectionRoutes {
    constructor(facet) {
      this.facet = facet;
      this.base = facet.dataset.scoochCollection;
      this.role = facet.dataset.scoochWebsiteRole;
      this.section = facet.getAttribute('section-id');
      this.status = document.getElementById('scooch-role-status-' + this.section);
      facet.scoochRoutes = this;
      this.events = new AbortController();
      for (const type of ['pagination:page-changed', 'facet:criteria-changed']) {
        facet.addEventListener(type, event => {
          event.stopImmediatePropagation();
          this.navigate(event.detail.url, true, true);
        }, {capture: true, signal: this.events.signal});
      }
      window.addEventListener('popstate', () => this.navigate(location.href, false, false), {signal: this.events.signal});
      if (facet.hasAttribute('data-scooch-role-pending')) this.navigate(location.href, false, false);
      else {this.normalize(facet);this.address(location.href, false);}
    }
    clean(value) {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !(url.pathname === this.base || url.pathname === this.base + '/' || url.pathname.startsWith(this.base + '/'))) throw Error('Invalid collection route');
      const tags = decodeURIComponent(url.pathname.slice(this.base.length).replace(/^\//, '')).split('+').filter(Boolean).filter(tag => !/^role_(website|parent|catalog|component)$/i.test(tag));
      url.pathname = this.base + (tags.length ? '/' + [...new Set(tags)].sort().map(encodeURIComponent).join('+') : '');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      return url;
    }
    internal(value) {
      const url = this.clean(value);
      const tags = url.pathname.slice(this.base.length).replace(/^\//, '');
      url.pathname = this.base + '/' + [this.role, ...tags.split('+').filter(Boolean)].sort().join('+');
      return url;
    }
    address(value, push) {
      const url = this.clean(value);
      const next = url.pathname + url.search + url.hash;
      if (next !== location.pathname + location.search + location.hash) history[push ? 'pushState' : 'replaceState'](history.state, '', next);
    }
    rendered() {
      this.normalize(this.facet);
      const toolbar = document.querySelector('.mobile-toolbar__item--filters');
      toolbar?.classList.toggle('has-filters', !!this.facet.querySelector('.product-facet__active-list'));
      // Notify external native controls without re-entering the facet renderer.
      document.dispatchEvent(new CustomEvent('facet:criteria-changed', {detail:{url:location.href}}));
    }
    normalize(root) {
      for (const link of root.querySelectorAll('a[href]:not([data-fallback])')) {
        const url = new URL(link.href, location.origin);
        if (url.origin !== location.origin || !url.pathname.startsWith(this.base + '/')) continue;
        const suffix = url.pathname.slice(this.base.length + 1);
        if (suffix.includes('/') || !/role_(website|parent|catalog|component)/i.test(suffix)) continue;
        const clean = this.clean(url);
        link.setAttribute('href', clean.pathname + clean.search + clean.hash);
      }
    }
    async navigate(value, push, scroll) {
      this.request?.abort();
      // Agent START: Consume only the matching initial request; superseding navigation aborts it.
      const url = this.internal(value);
      url.hash = '';
      url.searchParams.set('section_id', this.section);
      let prefetch = window.scoochCollectionPrefetch?.[this.section];
      if (prefetch) {
        delete window.scoochCollectionPrefetch[this.section];
        clearTimeout(prefetch.expiry);
        if (prefetch.url !== url.href) {
          prefetch.controller.abort();
          prefetch = null;
        }
      }
      const request = prefetch?.controller || new AbortController();
      // Agent END: One request owner per navigation.
      this.request = request;
      this.facet.dispatchEvent(new CustomEvent('facet:abort-loading', {bubbles:true}));
      this.facet.querySelector('scooch-compact-filters')?.request?.abort();
      this.facet.setAttribute('aria-busy', 'true');
      try {
        // Agent START: Read the prefetched body, retaining the ordinary navigation/error path.
        let html;
        if (prefetch) html = await prefetch.promise;
        else {
          const response = await fetch(url, {signal:request.signal, headers:{Accept:'text/html'}});
          if (!response.ok) throw Error('Collection unavailable');
          html = await response.text();
        }
        if (request.signal.aborted) return;
        if (html === null) throw Error('Collection unavailable');
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Agent END: Shared response validation below.
        const replacement = doc.querySelector('product-facet');
        if (!replacement || replacement.hasAttribute('data-scooch-role-pending') || !replacement.querySelector('#facet-main')) throw Error('Filtered collection unavailable');
        if (request.signal.aborted) return;
        this.address(value, push);
        this.facet.removeAttribute('data-scooch-role-pending');
        const scope = replacement.querySelector('scooch-compact-filters')?.dataset.enhancementScope;
        // Agent START: Preserve the compact filter's four-second fallback when inserted scripts cannot execute.
        if (scope) {
          const root = document.documentElement;
          const timers = window.scoochCompactFilterFallbackTimers = window.scoochCompactFilterFallbackTimers || {};
          root.classList.add(scope + '--enhancing');
          if (timers[scope]) clearTimeout(timers[scope]);
          timers[scope] = setTimeout(() => {
            if (!root.classList.contains(scope + '--active')) root.classList.remove(scope + '--enhancing');
            delete timers[scope];
          }, 4000);
        }
        // Agent END: Bounded compact-filter fallback for clean-route insertion.
        this.facet.innerHTML = replacement.innerHTML;
        this.rendered();
        if (this.status) this.status.hidden = true;
        if (scroll) requestAnimationFrame(() => this.facet.querySelector('#facet-main')?.scrollIntoView({block:'start',behavior:'instant'}));
      } catch (error) {
        if (error.name !== 'AbortError' && this.status) {
          this.status.hidden = false;
          const link = document.createElement('a');
          const fallback = this.internal(value);fallback.searchParams.delete('section_id');
          link.href = fallback.href;
          link.textContent = 'Load collection results';
          this.status.replaceChildren(document.createTextNode('Couldn’t update the collection. '), link);
        }
      } finally {
        if (this.request === request) this.facet.removeAttribute('aria-busy');
      }
    }
  }
  window.ScoochCollectionRoutes = CollectionRoutes;
  const mount = root => root.querySelectorAll('product-facet[data-scooch-website-role]').forEach(facet => {if (!facet.scoochRoutes) new CollectionRoutes(facet);});
  mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('product-facet').forEach(facet => {facet.scoochRoutes?.events.abort();facet.scoochRoutes?.request?.abort();}));
})();
/* Agent END: Clean collection route controller. */
